import { AimOutlined, GlobalOutlined } from '@ant-design/icons';
import { Select, Space } from 'antd';
import { type Map } from 'ol';
import type MousePosition from 'ol/control/MousePosition';
import { type Coordinate, format } from 'ol/coordinate';
import { type RefObject, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { GuardedMousePosition } from './GuardedMousePosition.ts';

const CoordinateControl = styled(Space)`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #fff;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  padding: 4px 12px;
`;

const ProjectionPrecision = {
  'EPSG:3857': 0,
  'EPSG:4326': 6,
};

type Projection = keyof typeof ProjectionPrecision;

interface Props {
  map: RefObject<Map>;
}

export function MapMousePositionControl(props: Props) {
  const { map } = props;

  const [mp, setMp] = useState<MousePosition>();
  const [projection, setProjection] = useState<Projection>('EPSG:3857');

  const coordinatesRef = useCallback((node: HTMLSpanElement) => {
    const mousePosition = new GuardedMousePosition({
      coordinateFormat: (coordinate?: Coordinate): string => {
        if (!coordinate) return '';
        return format(coordinate, '{x} {y}');
      },
      target: node,
      className: '',
      placeholder: '- -',
    });
    setMp(mousePosition);
  }, []);

  useEffect(() => {
    if (map.current && mp) {
      const controls = map.current.getControls();
      controls.push(mp);
      return () => {
        controls.remove(mp);
      };
    }
  }, [map, mp]);

  useEffect(() => {
    if (mp) {
      mp.setProjection(projection);
      mp.setCoordinateFormat((coordinate?: Coordinate): string => {
        if (!coordinate) return '';
        return format(coordinate, '{x} {y}', ProjectionPrecision[projection]);
      });
    }
  }, [mp, projection]);

  return (
    <Space>
      <CoordinateControl>
        <span>
          <AimOutlined />
        </span>
        <span>Coordinate:</span>
        <span ref={coordinatesRef} style={{ wordSpacing: '0.5em' }} />
      </CoordinateControl>

      <Select
        direction="rtl"
        placement="topLeft"
        suffixIcon={<GlobalOutlined />}
        value={projection}
        onChange={setProjection}
        options={[
          { label: 'EPSG: 3857', value: 'EPSG:3857' },
          { label: 'EPSG: 4326', value: 'EPSG:4326' },
        ]}
      />
    </Space>
  );
}
